
import { useToast } from "@/components/ui/use-toast";
import { 
  MessageSquare, 
  Users, 
  Clock, 
  Construction,
  Sparkles,
  Bell
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * Community page component that displays a "Coming Soon" message
 * while the full community features are being developed.
 * 
 * @returns JSX.Element - The rendered Community coming soon page
 */
const Community = () => {
  // Get toast functionality from the toast hook
  const { toast } = useToast();

  // Handler for notify me button
  const handleNotifyMe = () => {
    toast({
      title: "You're on the list!",
      description: "We'll notify you as soon as the Community Hub goes live.",
    });
  };

  return (
    <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center p-6">
      <div className="max-w-2xl mx-auto text-center space-y-8">
        {/* Main Coming Soon Card */}
        <Card className="p-8 bg-gradient-to-br from-primary/5 to-secondary/5 border-2 border-dashed border-primary/20">
          <CardHeader className="space-y-4">
            <div className="mx-auto w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
              <Construction className="w-10 h-10 text-primary" />
            </div>
            <div className="space-y-2">
              <CardTitle className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                Community Hub
              </CardTitle>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                Coming Soon
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <CardDescription className="text-lg text-gray-600">
              We're building something amazing! Soon you'll be able to connect with fellow learners, 
              share insights, and grow together in our vibrant community.
            </CardDescription>
            
            {/* Feature Preview Grid */}
            <div className="grid md:grid-cols-3 gap-4 mt-8">
              <div className="flex flex-col items-center p-4 bg-background/50 rounded-lg border">
                <MessageSquare className="w-8 h-8 text-primary mb-2" />
                <h3 className="font-semibold text-sm">Discussion Forums</h3>
                <p className="text-xs text-gray-500 text-center mt-1">
                  Share knowledge and ask questions
                </p>
              </div>
              
              <div className="flex flex-col items-center p-4 bg-background/50 rounded-lg border">
                <Users className="w-8 h-8 text-secondary mb-2" />
                <h3 className="font-semibold text-sm">Connect & Learn</h3>
                <p className="text-xs text-gray-500 text-center mt-1">
                  Find study buddies and mentors
                </p>
              </div>
              
              <div className="flex flex-col items-center p-4 bg-background/50 rounded-lg border">
                <Clock className="w-8 h-8 text-primary mb-2" />
                <h3 className="font-semibold text-sm">Real-time Updates</h3>
                <p className="text-xs text-gray-500 text-center mt-1">
                  Stay updated with community activity
                </p>
              </div>
            </div>
            
            {/* Notify Me Button */}
            <div className="pt-4">
              <Button 
                onClick={handleNotifyMe}
                className="gap-2 bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90"
                size="lg"
              >
                <Bell className="w-4 h-4" />
                Notify Me When It's Ready
              </Button>
            </div>
            
            {/* Timeline Hint */}
            <div className="text-sm text-gray-500 pt-2">
              Expected launch: Q1 2025
            </div>
          </CardContent>
        </Card>
        
        {/* Additional Info Card */}
        <Card className="bg-gradient-to-r from-gray-50 to-gray-100">
          <CardContent className="p-6">
            <div className="flex items-center justify-center gap-3 text-gray-600">
              <Sparkles className="w-5 h-5" />
              <p className="font-medium">
                In the meantime, keep learning and building your financial knowledge!
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Community;
